/**
 * Keeping a click-positioned popup inside the window.
 *
 * The variable editor is placed at the coordinates of the chip you clicked —
 * which is right until you click a chip near an edge. The panel is 260px wide
 * and several hundred tall, so a variable in the request-variables panel (which
 * parks itself bottom-RIGHT by design) opened its editor half outside the
 * window, with the value field unreachable (owner, 23.09.2026).
 *
 * Pure on purpose: jsdom measures every element as zero, so the only honest way
 * to test placement is to hand it the numbers.
 */

/** Distance kept from the window edge. */
export const VIEWPORT_MARGIN = 8;
/** Distance kept from the point that was clicked, so the popup never covers it. */
export const ANCHOR_GAP = 10;

/**
 * Where to draw a popup of `width`×`height` opened at (`x`, `y`).
 *
 * Horizontally it prefers its left edge on the point and slides left when that
 * would overflow — sliding rather than flipping, because a popup that jumps to
 * the other side of the cursor reads as a different popup.
 *
 * Vertically it prefers to hang below the point and FLIPS above it when there
 * is no room, which is what a dropdown does: clamping instead would park the
 * popup on top of the very chip you clicked.
 *
 * A popup taller than the window is pinned to the top margin rather than
 * centred — the header and the value field are what you came for, and they are
 * at the top.
 *
 * @returns {{left: number, top: number}} viewport coordinates for `position: fixed`
 */
export function placePopup({
  x,
  y,
  width,
  height,
  viewportWidth,
  viewportHeight,
  margin = VIEWPORT_MARGIN,
  gap = ANCHOR_GAP,
}) {
  const maxLeft = viewportWidth - width - margin;
  // `Math.min` first, then `Math.max`: with a popup wider than the window the
  // lower bound has to win, or it would be placed off the LEFT edge instead.
  const left = Math.max(margin, Math.min(x, maxLeft));

  const below = y + gap;
  const fitsBelow = below + height <= viewportHeight - margin;
  const above = y - gap - height;
  const fitsAbove = above >= margin;

  let top;
  if (fitsBelow) top = below;
  else if (fitsAbove) top = above;
  else top = Math.max(margin, viewportHeight - height - margin);

  return { left, top };
}

export default placePopup;
